import { CSSObject } from '@emotion/core';
import { noop } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { ProductDetail } from '@api/catalog';

import { Button, Layout, scale, typography, useTheme } from '@scripts/gds';

import ChevronLeft from '@icons/24/pagination-arrow-left.svg';
import ChevronRight from '@icons/24/pagination-arrow-right.svg';

export interface PaginationProps {
    /** Number of pages */
    pages: number;
    /** Base number of visible pages */
    baseNumberPages?: number;
    scrollToTop?: boolean;
    className?: string;
    isLoadMoreOnly?: boolean;
    isLoadMoreAvaible?: boolean;
    setProductDetailsData?: (arg: ProductDetail[]) => void;
    isVisibleShowMore?: boolean;
    currentPage?: number;
}

const Pagination = ({
    pages,
    baseNumberPages = 7,
    scrollToTop = false,
    className,
    isLoadMoreOnly = false,
    isLoadMoreAvaible = true,
    setProductDetailsData,
    isVisibleShowMore = false,
    currentPage,
}: PaginationProps) => {
    const { colors } = useTheme();

    const { pathname, query } = useRouter();

    const page = +(query.page || currentPage || 1);

    const getParams = (newPage: number, isLoadMoreAvaible: boolean = false) => ({
        pathname,
        query: {
            ...query,
            page: newPage,
            ...(isLoadMoreAvaible && { isLoadMoreAvaible: isLoadMoreAvaible }),
        },
    });

    const buttonCSS: CSSObject = !isLoadMoreOnly
        ? {
              width: scale(4),
              height: scale(4),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: `1px solid ${colors?.borderMain}`,
              borderRadius: '6px',
          }
        : {
              width: scale(17),
              height: scale(5),
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: `1px solid ${colors?.borderMain}`,
              borderRadius: '6px',
              padding: `${scale(3, true)}px ${scale(2, true) + 2}px`,
              textAlign: 'center',
              ...typography('button'),
          };

    const liCSS: CSSObject = !isLoadMoreOnly
        ? {
              width: scale(4),
              height: scale(4),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              a: buttonCSS,
          }
        : {
              width: scale(17),
              height: scale(5),
              a: buttonCSS,
          };

    // Находим средний элемент для корректного подсчета отклонений
    const centerIndex = Math.ceil(baseNumberPages / 2);

    // В реальном проекте следует заменять значениями констант, например enums
    const determinePosition = () => {
        if (pages > baseNumberPages) {
            if (page < centerIndex) return 'start';

            if (page > pages - centerIndex + 1) return 'end';

            return 'center';
        }

        return 'any';
    };

    const position = determinePosition();

    // Подсчет отображаемых страниц (отображаем либо максимально возможное, либо в зависимости от положения активной страницы)

    const visiblePages = position === 'any' ? pages : position === 'center' ? baseNumberPages : baseNumberPages - 1;

    // Функция определяет номер страницы для каждого элемента массива (подсчет ведется от 1го)
    const getItem = (pageNumber: number) => {
        if (position === 'any') return pageNumber;

        if (pageNumber === 1) return pageNumber;

        if (pageNumber === visiblePages) return pages;

        if ((pageNumber === 2 && position !== 'start') || (pageNumber === visiblePages - 1 && position !== 'end')) {
            return null;
        }

        if (position === 'center') {
            return page + pageNumber - centerIndex;
        }

        if (position === 'end') {
            return pages + pageNumber - visiblePages;
        }

        if (position === 'start') {
            return pageNumber;
        }

        return undefined;
    };

    const paginationArray = Array.from({ length: visiblePages }, (_, i) => getItem(i + 1));

    const scrollToTopCustom = () => {
        window.scrollTo({ top: 300, behavior: 'smooth' });
    };

    useEffect(() => {
        const scrollToTopCustom = () => {
            window.scrollTo({ top: 300, behavior: 'smooth' });
        };

        return () => {
            window.removeEventListener('scroll', scrollToTopCustom);
        };
    }, []);

    return (
        <>
            {page !== pages && isVisibleShowMore && (
                <Layout.Item col={{ xxxl: 4, lg: 3, xs: 2 }}>
                    <Link
                        legacyBehavior
                        href={getParams(page + 1, isLoadMoreAvaible)}
                        passHref
                        prefetch={false}
                        scroll={scrollToTop}
                    >
                        <Button
                            data-testid="button-expand"
                            theme="outlineSecondaryGrey"
                            size="md"
                            onClick={() => getParams(page + 1)}
                            css={{
                                width: '100%',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                display: isLoadMoreAvaible ? 'block' : 'none',
                            }}
                        >
                            Загрузить еще
                        </Button>
                    </Link>
                </Layout.Item>
            )}
            <ul
                css={{
                    marginTop: scale(7, true),
                    display: 'grid',
                    gridAutoFlow: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gridGap: scale(1),
                }}
                className={className}
            >
                <li
                    css={liCSS}
                    onClick={() => {
                        setProductDetailsData && page !== 1 ? scrollToTopCustom() : noop;
                        setProductDetailsData && page !== 1 ? setProductDetailsData([]) : noop;
                    }}
                >
                    <Link legacyBehavior href={getParams(page - 1)} passHref prefetch={false}>
                        <a
                            aria-label={'влево'}
                            css={{
                                ...buttonCSS,
                                ...(page === 1 && {
                                    fill: colors?.grey600,
                                    pointerEvents: 'none',
                                }),
                                '&:hover': {
                                    fill: colors?.primaryHover,
                                },
                            }}
                        >
                            <ChevronLeft />
                        </a>
                    </Link>
                </li>

                {!isLoadMoreOnly &&
                    paginationArray.map((item, index) => (
                        <li
                            onClick={() => {
                                scrollToTopCustom();
                                setProductDetailsData && item !== page ? setProductDetailsData([]) : noop;
                            }}
                            key={index}
                            css={{ ...typography('button'), ...liCSS }}
                        >
                            {item ? (
                                item === page ? (
                                    <span
                                        css={{
                                            ...buttonCSS,
                                            ...typography('bodySm'),
                                            fill: colors?.grey900,
                                            backgroundColor: colors?.textMainBlack,
                                            color: colors?.white,
                                            borderRadius: '6px',
                                            cursor: 'default',
                                        }}
                                    >
                                        {item}
                                    </span>
                                ) : (
                                    <Link legacyBehavior href={getParams(item)} passHref prefetch={false}>
                                        <a
                                            aria-label={'Ссылка на страницу'}
                                            css={{
                                                ...typography('bodySm'),
                                                ...buttonCSS,
                                                '&:hover': {
                                                    border: `1px solid ${colors?.primaryHover}`,
                                                },
                                            }}
                                        >
                                            {item}
                                        </a>
                                    </Link>
                                )
                            ) : (
                                <span css={typography('bodySm')}>...</span>
                            )}
                        </li>
                    ))}
                <li
                    css={{ ...liCSS }}
                    onClick={() => {
                        setProductDetailsData && page !== pages ? scrollToTopCustom() : noop;
                        setProductDetailsData && page !== pages ? setProductDetailsData([]) : noop;
                    }}
                >
                    <Link legacyBehavior href={getParams(page + 1)} passHref prefetch={false}>
                        <a
                            aria-label={'вправо'}
                            css={{
                                ...buttonCSS,
                                ...(page === pages && {
                                    fill: colors?.grey600,
                                    pointerEvents: 'none',
                                }),
                                '&:hover': {
                                    fill: colors?.primaryHover,
                                },
                                display: isLoadMoreAvaible ? 'block' : 'none',
                            }}
                        >
                            <ChevronRight />
                        </a>
                    </Link>
                </li>
            </ul>
        </>
    );
};

export default Pagination;
